import { useRef, useEffect, useState, useMemo } from 'react';
import jwtDecode from 'jwt-decode';
import { parseISO } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';

import Translator from '../../translator.js';
import { setAlert } from '../../actions/alert';
import {
  formatDateToISOWithoutZ,
  getSelectableDateBounds,
  validateSelectedDate,
  validateDateTime,
} from '../../../utils/fermer-enregistrement.js';
import { postFemerEnregistrement } from '../../../server/services/fermer-enregistrement-service.js';

export function useFermerEnregistrementForm({ location, idEnregistrementEntete: idFromStore, language }) {
  const calendarRef = useRef(null);
  const TEXTS = useMemo(() => Translator(language, 'TXT_FERMER_ENREGISTREMENT_PAGE'), [language]);

  const [heure, setHeure] = useState('');
  const [date, setDate] = useState(null);
  const [load, setLoad] = useState(false);
  const [minutes, setMinutes] = useState('');
  const [heureIsSlected, setHeureIsSlected] = useState(false);
  const [dateIsSelected, setdateIsSelected] = useState(false);
  const [isOpenCalendar, setIsOpenCalendar] = useState(false);
  const [enregistrementEntete, setenregistrementEntete] = useState('');
  const [dateErrorKey, setDateErrorKey] = useState('');

  const dispatch = useDispatch();
  const { authToken } = useSelector(state => state);

  const dateArrivee = location?.state?.dateArrive ? parseISO(location.state.dateArrive) : null;
  const dateDepart = location?.state?.dateDepart ? parseISO(location.state.dateDepart) : null;
  const { minSelectableDate, maxSelectableDate, maxDateTime, today } = getSelectableDateBounds(dateArrivee, dateDepart);

  useEffect(() => {
    const id = idFromStore || location?.state?.idEnregistrementEntete;
    if (id && id !== '' && id !== undefined) {
      setenregistrementEntete(id);
    }
  }, [idFromStore, location?.state?.idEnregistrementEntete]);

  useEffect(() => {
    const handleClickOutsideCalendar = (event) => {
      if (calendarRef.current && isOpenCalendar && !calendarRef.current.contains(event.target)) {
        setIsOpenCalendar(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutsideCalendar);

    return () => {
      document.removeEventListener('mousedown', handleClickOutsideCalendar);
    };
  }, [isOpenCalendar]);

  const handleDateChange = (dateValue) => {
    const errorKey = validateSelectedDate({
      dateValue,
      dateArrivee,
      minSelectableDate,
      maxSelectableDate,
      today,
    });
    if (errorKey) {
      setDateErrorKey(errorKey);
      return false;
    }
    setDateErrorKey('');
    setDate(dateValue);
    setHeureIsSlected(false);
    return true;
  };

  const handleTimeChange = (heureValue, minutesValue) => {
    if (!isNaN(heureValue) && !isNaN(minutesValue) && heureValue >= 0 && heureValue < 24 && minutesValue >= 0 && minutesValue < 60) {
      const errorKey = validateDateTime({
        dateValue: date,
        heureValue,
        minutesValue,
        dateArrivee,
        maxDateTime,
      });
      if (errorKey) {
        setDateErrorKey(errorKey);
        setHeureIsSlected(false);
        return;
      }
      setDateErrorKey('');
      setHeure(heureValue);
      setMinutes(minutesValue);
      setHeureIsSlected(true);
    } else {
      dispatch(setAlert(TEXTS.errorInvalidTime, 'danger'));
    }
  };

  const buildDateFermetureISO = () => {
    const dateFermeture = new Date(date);
    dateFermeture.setHours(heure, minutes);
    return formatDateToISOWithoutZ(dateFermeture);
  };

  const submitFermeture = async (buildPayload) => {
    const errorKey = validateDateTime({
      dateValue: date,
      heureValue: heure,
      minutesValue: minutes,
      dateArrivee,
      maxDateTime,
    });
    if (errorKey) {
      setDateErrorKey(errorKey);
      return false;
    }

    setLoad(true);
    try {
      if (authToken != null) {
        const decodedToken = jwtDecode(authToken);
        const dateFermetureISO = buildDateFermetureISO();
        const payload = buildPayload(decodedToken, dateFermetureISO);
        const response = await postFemerEnregistrement(payload);

        if (response.ok) {
          dispatch(setAlert(TEXTS.successClose, 'success'));
          return true;
        } else {
          const errorData = await response.json();
          const errorMessage = errorData.detail || TEXTS.errorGeneric;
          dispatch(setAlert(errorMessage, 'danger'));
          return false;
        }
      }
    } catch (error) {
      dispatch(setAlert(TEXTS.errorUnexpected, 'danger'));
      return false;
    } finally {
      setLoad(false);
    }

    return false;
  };

  return {
    TEXTS,
    calendarRef,
    heure,
    date,
    load,
    setLoad,
    minutes,
    heureIsSlected,
    dateIsSelected,
    setdateIsSelected,
    isOpenCalendar,
    setIsOpenCalendar,
    enregistrementEntete,
    dateErrorKey,
    dateArrivee,
    dateDepart,
    minSelectableDate,
    maxSelectableDate,
    authToken,
    handleDateChange,
    handleTimeChange,
    submitFermeture,
  };
}
